import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { CTAPair } from "@/components/CTAButtons";
import { SITE } from "@/data/site";

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true"><path d="M13.5 21v-7.5h2.5l.4-3h-2.9V8.6c0-.9.3-1.5 1.5-1.5h1.6V4.4c-.3 0-1.2-.1-2.3-.1-2.3 0-3.8 1.4-3.8 3.9v2.3H8v3h2.5V21z" /></svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true"><path d="M17.8 3h3.1l-6.8 7.7L22 21h-6.2l-4.9-6.4L5.3 21H2.2l7.3-8.3L2 3h6.4l4.4 5.8zm-1.1 16.2h1.7L7.4 4.7H5.6z" /></svg>
);

export const SocialLinks = ({ className = "" }) => (
  <div className={`flex flex-col gap-3 ${className}`}>
    <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" data-testid="facebook-link"
      className="inline-flex items-center gap-3 rounded-full border border-cream/20 px-4 py-2.5 text-sm text-cream/85 transition-colors hover:border-cream hover:text-cream">
      <FacebookIcon /> Facebook <span className="font-mono text-[11px] text-cream/70">{SITE.facebookLabel}</span>
    </a>
    <a href={SITE.twitter} target="_blank" rel="noopener noreferrer" data-testid="twitter-link"
      className="inline-flex items-center gap-3 rounded-full border border-cream/20 px-4 py-2.5 text-sm text-cream/85 transition-colors hover:border-cream hover:text-cream">
      <XIcon /> Twitter <span className="font-mono text-[11px] text-cream/70">{SITE.twitterHandle}</span>
    </a>
  </div>
);

export const Footer = () => (
  <footer className="relative overflow-hidden bg-cocoa pb-28 pt-24 text-cream md:pb-12" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-mango">¡Te esperamos!</p>
          <h2 className="mt-4 font-display text-4xl leading-[1.02] sm:text-6xl">
            Hungry? <em className="text-mango">Call it in,</em><br />pick it up hot.
          </h2>
          <CTAPair prefix="footer" className="mt-8" />
        </div>
        <div className="grid gap-10 sm:grid-cols-3 lg:col-span-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60">Find us</p>
            <p className="mt-3 text-sm leading-relaxed text-cream/90" data-testid="footer-address">{SITE.street}<br />{SITE.cityLine}</p>
            <a href={SITE.tel} className="mt-3 block text-sm text-mango hover:underline" data-testid="footer-phone">{SITE.phone}</a>
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60">Horario</p>
            <p className="mt-3 text-sm leading-relaxed text-cream/90" data-testid="footer-hours">Mon–Sat<br />10:00 AM – 6:00 PM<br />Sunday closed</p>
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60">Pages</p>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link to="/" className="text-cream/90 hover:text-mango" data-testid="footer-home-link">Home</Link></li>
              <li><Link to="/menu" className="text-cream/90 hover:text-mango" data-testid="footer-menu-link">Menu</Link></li>
              <li><Link to="/visit" className="text-cream/90 hover:text-mango" data-testid="footer-visit-link">Visit Us</Link></li>
            </ul>
          </div>
        </div>
      </div>
      <div className="mt-16 flex flex-col gap-8 border-t border-cream/10 pt-10 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <LogoMark className="h-12 w-12" />
          <div>
            <p className="font-display text-2xl">Sabor Latino</p>
            <p className="flex items-center gap-1.5 text-sm text-cream/75"><Star className="h-4 w-4 fill-mango text-mango" /> {SITE.rating} · {SITE.reviews} Google reviews</p>
          </div>
        </div>
        <SocialLinks className="sm:flex-row" />
      </div>
      <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/50">Website preview · Dominican & Puerto Rican kitchen · Grand Rapids, MI</p>
    </div>
    <p aria-hidden="true" className="pointer-events-none absolute -bottom-[0.25em] left-0 right-0 select-none text-center font-display text-[22vw] italic leading-none text-cream/[0.04]">Sabor</p>
  </footer>
);
